import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { Children, isValidElement, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import WdSvgIcon from '@main/core/WdSvgIcon';

const handleValidateImage = (image, host) => {
  if (!image) return undefined;

  const path = image.fullPathThumb || image.fullPath;
  if (!path) return undefined;

  const normalizedHost = host ?? '';
  const separator = host ? '/' : '';
  return `${normalizedHost}${separator}${path}`;
};

const Item = ({
  to,
  icon,
  title = 'item',
  role = 'button',
  onClick = () => {},
  needsAuth = false,
  ...props
}) => {
  return (
    <MenuItem component={Link} to={to} onClick={onClick} role={role} {...props}>
      {icon !== undefined && icon !== null && (
        <ListItemIcon className="min-w-40">
          {typeof icon === 'string' && <WdSvgIcon>{icon}</WdSvgIcon>}
          {isValidElement(icon) && icon}
        </ListItemIcon>
      )}
      <ListItemText primary={title} />
    </MenuItem>
  );
};

/**
 * Componente de menú desplegable para el usuario, que muestra su avatar, nombre, correo, y opciones de navegación.
 * Incluye lógica condicional para usuarios autenticados y no autenticados.
 *
 * @component
 * @param {Object} props - Props del componente.
 * @param {Boolean} props.authenticated - Está autenticado o no.
 * @param {string} props.baseUrl - URL base para construir la ruta de la imagen del avatar.
 * @param {Object} props.user - Objeto de usuario.
 * @param {string} [props.user.name] - Nombre completo del usuario.
 * @param {string} [props.user.email] - Correo electrónico del usuario.
 * @param {string} [props.user.backgroundColor] - Color de fondo para el `Avatar` en caso de no tener imagen.
 * @param {Object|null} [props.user.avatar] - Objeto del avatar, puede contener `fullPathThumb` o `fullPath`.
 * @param {React.ReactNode} [props.children] - Elementos hijos adicionales para incluir dentro del popover.
 *
 * @returns {JSX.Element}
 */
function UserMenu({ baseUrl, user, authenticated = false, children, ...props }) {
  const childrenArray = Children.toArray(children);
  const { name, avatar, email, backgroundColor } = user || {};

  const [profileMenu, setProfileMenu] = useState(null);

  const profileMenuClick = (event) => {
    setProfileMenu(event.currentTarget);
  };

  const profileMenuClose = () => {
    setProfileMenu(null);
  };

  const filteredChildren = useMemo(() => {
    const attachCloseHandler = (child) => {
      if (!isValidElement(child)) return child;

      const originalOnClick = child.props.onClick;

      return {
        ...child,
        props: {
          ...child.props,
          onClick: (e) => {
            if (typeof originalOnClick === 'function') {
              originalOnClick(e);
            }
            profileMenuClose();
          },
        },
      };
    };

    return childrenArray
      .filter((child) => {
        if (!isValidElement(child)) return false;
        const itemNeedsAuth = child.props?.needsAuth ?? false;
        return authenticated ? itemNeedsAuth === true : itemNeedsAuth === false;
      })
      .map((child) => attachCloseHandler(child));
  }, [authenticated, childrenArray]);

  return (
    <>
      <Button
        className="min-h-40 min-w-40 px-0 md:px-16 py-0 md:py-6"
        onClick={profileMenuClick}
        color="inherit"
        {...props}
      >
        {authenticated === true && (
          <div className="hidden md:flex flex-col mx-4 items-end">
            {typeof name === 'string' && (
              <Typography component="span" className="font-semibold flex">
                {name && name.split(' ').slice(0, 3).join(' ')}
              </Typography>
            )}
            <Typography className="text-11 font-medium" color="text.secondary">
              {email || 'Invitado'}
            </Typography>
          </div>
        )}
        <Avatar
          sx={{
            backgroundColor,
          }}
          className="avatar md:mx-4"
          alt={name}
          src={handleValidateImage(avatar, baseUrl)}
        >
          {typeof name === 'string' &&
            name
              .split(' ')
              .map((word) => word[0])
              .join('')
              .substring(0, 2)
              .toUpperCase()}
        </Avatar>
      </Button>

      <Popover
        open={Boolean(profileMenu)}
        anchorEl={profileMenu}
        onClose={profileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        classes={{
          paper: 'py-8',
        }}
      >
        {filteredChildren}
      </Popover>
    </>
  );
}

/**
 * Componente individual de un menú, renderizado como un `MenuItem` de Material UI.
 *
 * @component
 * @param {Object} props - Props del componente.
 * @param {string} props.to - Ruta de navegación, pasada al componente `Link`.
 * @param {React.ReactNode | string} [props.icon] - Ícono a mostrar; puede ser un string (nombre de ícono SVG) o un React Element.
 * @param {string} [props.title='item'] - Texto principal del ítem.
 * @param {string} [props.role='button'] - Rol ARIA para accesibilidad.
 * @param {Function} [props.onClick=() => {}] - Callback a ejecutar cuando se hace clic en el ítem.
 * @param {Object} [props.props] - Cualquier otro prop adicional que será propagado al componente `MenuItem`.
 *
 * @returns {JSX.Element}
 */
UserMenu.Item = Item;

export default UserMenu;
